import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, View } from './components/base_components';
import Breadcrumb from './components/breadcrumb';
import ImageButton from './components/buttons/image_button';
import SelectBar from './components/select_bar';
import StandardBoxList from './components/standard_box_list';
import TitleActionLine from './components/title_action_line';
import VolumeTabBar from './components/volume_tab_bar';
import Chart, { ChartOptions, SeriesOptionsType } from './components/chart';

import UserStore from './stores/user_store';
import UsageStore from './stores/usage_store';
import VolumeStore from './stores/volume_store';

import { byteFormat } from './tools/util';

const styles = StyleSheet.create({
  volumeMetricScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  tabBar: {
    marginLeft: '2rem',
    marginRight: '2rem',
  },
  topBar: {
    marginTop: '1.5rem',
    marginRight: '2rem',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  timeBar: {
    marginRight: '1rem',
    alignSelf: 'flex-end',
  },
  reloadButton: {
    height: '1.5rem',
    width: '3.5rem',
    padding: '0.75rem',
    borderWidth: 1,
    borderColor: '#677078',
    borderRadius: '0.3rem',
    filter: 'var(--icon-grey)',
  },
  chartView: {
    margin: '1rem',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  chart: {
    margin: '1rem',
    flex: 1,
    height: '55rem',
    minWidth: '40rem',
  },
});

const DAY = 24 * 60 * 60 * 1000;
const RELOAD = require('./assets/img/reload_black.png');
const TIME_OPTS = [
  { value: 7, text: '1w' },
  { value: 14, text: '2w' },
  { value: 30, text: '30d' },
  { value: 90, text: '90d' },
];
const POINT_WIDTH_MAP = {
  7: 30,
  14: 10,
  30: 6,
  90: 2,
};
const TICK_INTERVAL_MAP = {
  7: 1 * DAY,
  14: 3 * DAY,
  30: 7 * DAY,
  90: 14 * DAY,
};
export default function VolumeMetricScreen(props) {
  const params = useParams();
  const org = UserStore.useOrg();
  const volume_id = parseInt(params.volume_id ?? '0', 10) || 0;
  const volume = VolumeStore.useVolume(org?.org_id, volume_id);
  const [time, setTime] = React.useState(7);
  const usage_data = UsageStore.useUsage(volume_id);

  React.useEffect(() => {
    VolumeStore.fetch(org?.org_id);
  }, [org?.org_id]);
  React.useEffect(() => {
    UsageStore.fetch({ org_id: org?.org_id, volume_id });
  }, [org?.org_id, volume_id]);

  function _onReloadPress() {
    UsageStore.fetch({ org_id: org?.org_id, volume_id });
  }

  let count_options: ChartOptions | null = null;
  let size_options: ChartOptions | null = null;
  let s3_options: ChartOptions | null = null;
  if (usage_data) {
    const { s3_list, usage_list } = usage_data;
    const buckets = new Map();
    let max = 0;
    s3_list.forEach((usage) => {
      buckets.set(usage.s3_bucket_id, usage.s3_bucket_name);
      max = Math.max(max, usage.usage_date);
    });
    max += 0.5 * DAY;
    const ids = [...buckets.keys()];
    const s3_series = ids.map((id) =>
      _makeS3Series(buckets.get(id), s3_list, id)
    );
    s3_options = _makeOptions(time, true, s3_series);

    const active_bytes = _makeUsageSeries(
      'Active Bytes',
      usage_list,
      (d) => d.live_bytes
    );
    const inactive_bytes = _makeUsageSeries(
      'Inactive Bytes',
      usage_list,
      (d) => d.total_bytes - d.live_bytes
    );
    size_options = _makeOptions(time, true, [active_bytes, inactive_bytes]);

    const active_files = _makeUsageSeries(
      'Active Files',
      usage_list,
      (d) => d.live_count
    );
    const inactive_files = _makeUsageSeries(
      'Inactive Files',
      usage_list,
      (d) => d.total_count - d.live_count
    );
    count_options = _makeOptions(time, false, [active_files, inactive_files]);
  }

  return (
    <View style={styles.volumeMetricScreen}>
      <Breadcrumb
        list={[
          { text: 'Volumes', url: '/volume' },
          { text: volume?.volume_name, url: `/volume/${volume_id}` },
          'Metrics',
        ]}
      />
      <TitleActionLine title={volume?.volume_name} />
      <VolumeTabBar style={styles.tabBar} volumeId={volume_id} />
      <StandardBoxList title='Volume Metrics'>
        <View style={styles.topBar}>
          <SelectBar
            style={styles.timeBar}
            options={TIME_OPTS}
            value={time}
            onChange={setTime}
          />
          <ImageButton
            style={styles.reloadButton}
            underlayColor='rgba(0,0,0,0.07)'
            source={RELOAD}
            onPress={_onReloadPress}
          />
        </View>
        <View style={styles.chartView}>
          <Chart
            style={styles.chart}
            title='Volume Size'
            detail='Size of files in the volume.'
            options={size_options}
          />
          <Chart
            style={styles.chart}
            title='Cloud storage usage'
            detail='Amount of bytes stored in cloud storage each day.'
            options={s3_options}
          />
          <Chart
            style={styles.chart}
            title='File Count'
            detail='Number files in volume.'
            options={count_options}
          />
        </View>
      </StandardBoxList>
    </View>
  );
}
function _makeS3Series(name, list, id): SeriesOptionsType {
  return {
    name,
    type: 'column',
    data: list
      .filter((r) => r.s3_bucket_id === id)
      .map((r) => ({ x: r.usage_date, y: r.byte_days }))
      .sort(_sortX),
  };
}
function _makeUsageSeries(name, list, cb): SeriesOptionsType {
  return {
    name,
    type: 'column',
    data: list.map((r) => ({ x: r.usage_date, y: cb(r) })).sort(_sortX),
  };
}
function _makeOptions(
  time: number,
  is_bytes: boolean,
  series: any
): ChartOptions {
  let max = 0;
  series.forEach((s) => {
    s.data.forEach((p) => (max = Math.max(max, p.x)));
  });
  max += 0.5 * DAY;
  return {
    series,
    plotOptions: {
      column: {
        stacking: 'normal',
        pointWidth: POINT_WIDTH_MAP[time],
      },
    },
    tooltip: {
      style: {
        fontSize: '1.3rem',
      },
    },
    legend: {
      itemStyle: {
        fontSize: '1.2rem',
      },
    },
    yAxis: {
      title: {
        text: is_bytes ? 'Bytes' : 'Count',
        style: {
          color: '#666666',
          fontSize: '1.4rem',
        },
      },
      labels: {
        style: {
          color: '#545b64',
          fontSize: '1.2rem',
        },
        formatter: is_bytes
          ? function () {
              return byteFormat(this.value as number, 1);
            }
          : undefined,
      },
      tickAmount: 6,
    },
    xAxis: {
      type: 'datetime',
      labels: {
        style: {
          color: '#545b64',
          fontSize: '1.2rem',
        },
        format: '{value:%m/%d}',
      },
      tickInterval: TICK_INTERVAL_MAP[time],
      min: max - time * DAY,
      max,
    },
  };
}
function _sortX(a, b) {
  return a.x - b.x;
}
